import { Check, MoreHorizontal, XCircle } from 'lucide-react';

import { cn } from '@/shared/lib/utils/classnames';
import type { AgentExecutionState, AgentExecutionStep } from '@/shared/types/execution';
import { Progress } from '@/shared/ui/progress';

export interface ExecutionProgressProps {
  executionState: AgentExecutionState;
  /**
   * Agent 실행 단계 목록
   * 제공하지 않으면 기본 단계를 사용합니다
   */
  steps?: AgentExecutionStep[];
}

/**
 * 기본 실행 단계 (steps가 제공되지 않을 때 사용)
 */
const DEFAULT_EXECUTION_STEPS: AgentExecutionStep[] = [
  { id: 'validate', label: '입력 검증', order: 1 },
  { id: 'preprocess', label: '전처리', order: 2 },
  { id: 'analyze', label: '분석', order: 3 },
  { id: 'generate', label: '결과 생성', order: 4 },
  { id: 'postprocess', label: '후처리', order: 5 },
];

/**
 * 현재 진행중인 스텝의 상태 메시지 생성
 */
function getStatusMessage(
  executionSteps: AgentExecutionStep[],
  currentStepId: string | null,
  isRunning: boolean,
  hasResult: boolean,
  hasError: boolean,
): string {
  if (hasError) return '오류가 발생했습니다';
  if (hasResult) return '실행 완료';
  if (!isRunning) return '실행 대기 중';

  const currentStep = executionSteps.find((s) => s.id === currentStepId);
  if (currentStep) {
    return `${currentStep.label} 중..`;
  }
  return '실행 중..';
}

/**
 * 완료된 스텝 수 계산
 */
function getCompletedCount(
  executionSteps: AgentExecutionStep[],
  progressMap: Map<string, { status: string }>,
): number {
  return executionSteps.filter((step) => progressMap.get(step.id)?.status === 'completed').length;
}

/**
 * 실행 진행 상황 표시 컴포넌트
 *
 * Agent 실행의 각 단계별 진행 상황을 시각적으로 표시합니다.
 * - 상단: 상태 메시지 + 진행률 카운터 (X/Y)
 * - 프로그레스 바
 * - 스텝 목록 (대기/실행중/완료/실패 상태별 스타일링)
 *
 * @example
 * ```tsx
 * <ExecutionProgress
 *   executionState={executionState}
 *   steps={customSteps}
 * />
 * ```
 */
export function ExecutionProgress({ executionState, steps }: ExecutionProgressProps) {
  const { steps: progressMap, currentStepId, error, isRunning, result } = executionState;
  const executionSteps = steps || DEFAULT_EXECUTION_STEPS;

  const totalSteps = executionSteps.length;
  const completedCount = getCompletedCount(executionSteps, progressMap);
  const progressPercentage = totalSteps > 0 ? (completedCount / totalSteps) * 100 : 0;

  const statusMessage = getStatusMessage(
    executionSteps,
    currentStepId,
    isRunning,
    result !== null,
    error !== null,
  );

  return (
    <div className="space-y-5" data-testid="execution-progress">
      {/* 프로그레스 헤더: 상태 메시지 + 카운터 */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-base text-foreground">{statusMessage}</span>
          <span className="text-base font-medium text-foreground">
            {completedCount} / {totalSteps}
          </span>
        </div>

        {/* 프로그레스 바 */}
        <Progress value={progressPercentage} className="h-4 rounded bg-[#e6f1ff]" />
      </div>

      {/* 스텝 목록 */}
      <div className="space-y-2">
        {executionSteps.map((step) => {
          const progress = progressMap.get(step.id);
          // isActive는 두 가지 방식으로 판단:
          // 1. currentStepId가 일치하는 경우 (서버에서 stepId를 정확히 보내는 경우)
          // 2. progress.status가 'running'인 경우 (currentStepIndex 기반으로 계산된 경우)
          const isActive = currentStepId === step.id || progress?.status === 'running';
          const isCompleted = progress?.status === 'completed';
          const isFailed = progress?.status === 'failed';
          const isPending = !progress || progress.status === 'pending';

          return (
            <StepItem
              key={step.id}
              label={step.label}
              isCompleted={isCompleted}
              isActive={isActive}
              isFailed={isFailed}
              isPending={isPending}
            />
          );
        })}
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div className="mt-4 rounded-lg border border-destructive/30 bg-destructive/10 p-4">
          <div className="flex items-start gap-2">
            <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
            <div>
              <p className="text-sm font-semibold text-destructive">{error.code}</p>
              <p className="mt-1 text-sm text-destructive/80">{error.message}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * 스텝 아이템 컴포넌트
 */
interface StepItemProps {
  label: string;
  isCompleted: boolean;
  isActive: boolean;
  isFailed: boolean;
  isPending: boolean;
}

function StepItem({ label, isCompleted, isActive, isFailed, isPending }: StepItemProps) {
  // 배경색 결정: active는 회색 배경 (shimmer가 파란색으로 움직임)
  const bgColor = isPending || isActive ? 'bg-[#f6f6f6]' : 'bg-[#e6f1ff]';

  // 텍스트 색상 결정: active는 완료된 스텝과 동일
  const textColor = isPending ? 'text-[#9ca3af]' : 'text-foreground';

  // 아이콘 색상 결정: active는 더 연한 회색 (미완료 체크 아이콘)
  const iconColor = isActive
    ? 'text-[#d1d5db]'
    : isPending
      ? 'text-[#9ca3af]'
      : isFailed
        ? 'text-destructive'
        : 'text-[#0084ff]';

  // 아이콘 우선순위: completed > failed > active (disabled check) > pending
  const showCheck = isCompleted;
  const showFailed = !isCompleted && isFailed;
  const showActiveCheck = !isCompleted && !isFailed && isActive; // 스피너 대신 disabled 체크
  const showPending = !isCompleted && !isFailed && !isActive && isPending;

  return (
    <div
      className={cn(
        'relative flex items-center gap-3 overflow-hidden rounded-md px-4 py-3',
        bgColor,
      )}
      data-testid="step-item"
      data-status={
        isCompleted ? 'completed' : isActive ? 'running' : isFailed ? 'failed' : 'pending'
      }
    >
      {/* Shimmer overlay for active step */}
      {isActive && (
        <div className="animate-shimmer pointer-events-none absolute inset-0" aria-hidden="true" />
      )}

      {/* 아이콘 */}
      <div className={cn('relative shrink-0', iconColor)}>
        {showCheck && <Check className="h-5 w-5" strokeWidth={2.5} />}
        {showActiveCheck && <Check className="h-5 w-5" strokeWidth={2.5} />}
        {showFailed && <XCircle className="h-5 w-5" />}
        {showPending && <MoreHorizontal className="h-5 w-5" />}
      </div>

      {/* 라벨 */}
      <span className={cn('relative text-base', textColor)}>{label}</span>
    </div>
  );
}
